import { useCountUp } from '@/hooks/useCountUp';

export interface StatRowItem {
  /** Display value when no numeric is given (e.g. "—"). */
  value: string;
  /**
   * Numeric value used for the count-up animation. When provided, the
   * displayed number animates from 0 → numericValue with ease-out cubic.
   * The `value` prop becomes the formatted shell (e.g. "$X" / "X bookings").
   */
  numericValue?: number;
  /** Currency prefix (e.g. "$") used when numericValue is set. */
  currency?: boolean;
  /** Sub-label, e.g. "Active quotes", "This week", "Needs you". */
  label: string;
  /** When true, renders as the amber attention variant (FEF3C7 bg, B45309 num). */
  attention?: boolean;
  /** Optional tap handler — when present, the tile renders as a <button>
   *  and navigates / fires the callback on tap. */
  onClick?: () => void;
  /** Accessible label override for the tap action. */
  ariaLabel?: string;
}

interface StatRowProps {
  items: StatRowItem[];
}

function StatNumber({
  numericValue,
  fallback,
  currency,
  attention,
}: {
  numericValue?: number;
  fallback: string;
  currency?: boolean;
  attention?: boolean;
}) {
  const animated = useCountUp(numericValue ?? 0, { durationMs: 750 });
  if (numericValue === undefined) {
    return (
      <span
        className={
          attention ? 'text-[22px] font-bold' : 'text-[22px] font-bold text-ink-900 dark:text-[#E2E8F0]'
        }
        style={attention ? { color: '#B45309' } : undefined}
      >
        {fallback}
      </span>
    );
  }
  const display = currency
    ? `$${Math.round(animated).toLocaleString()}`
    : Math.round(animated).toLocaleString();
  return (
    <span
      className={
        attention
          ? 'text-[22px] font-bold tabular-nums'
          : 'text-[22px] font-bold tabular-nums text-ink-900'
      }
      style={attention ? { color: '#B45309' } : undefined}
    >
      {display}
    </span>
  );
}

/**
 * StatRow — three-card horizontal grid below the hero card on Today.
 * Numbers count up smoothly on mount + when they change so the screen
 * always feels alive.
 */
export function StatRow({ items }: StatRowProps) {
  return (
    <div className="grid grid-cols-3 gap-2.5 px-4 py-4">
      {items.map((it, i) => {
        const tileClass = [
          'ss-press-light rounded-ss-md border px-3 py-3.5 text-center animate-ss-fade-in',
          it.attention
            ? ''
            : 'border-ink-200 dark:border-[#334155] bg-white dark:bg-[#1E293B] shadow-ss-sm',
        ].join(' ');
        const tileStyle = it.attention
          ? { background: '#FEF3C7', borderColor: '#FDE68A' }
          : undefined;
        const tileInner = (
          <>
            <div className="leading-none">
              <StatNumber
                numericValue={it.numericValue}
                fallback={it.value}
                currency={it.currency}
                attention={it.attention}
              />
            </div>
            <div className="mt-1 text-[11px] font-medium text-ink-500 dark:text-[#94A3B8]">
              {it.label}
            </div>
          </>
        );
        return it.onClick ? (
          <button
            key={i}
            type="button"
            onClick={it.onClick}
            aria-label={it.ariaLabel ?? it.label}
            className={`${tileClass} cursor-pointer focus:outline-none focus:ring-2 focus:ring-ss-blue/30`}
            style={tileStyle}
          >
            {tileInner}
          </button>
        ) : (
          <div key={i} className={tileClass} style={tileStyle}>
            {tileInner}
          </div>
        );
      })}
    </div>
  );
}
